import printGlyphs from "./printGlyphs.json";

/**
 * What prints for a piece of customer text, so previews show the same characters as the book.
 *
 * A port of the text handling in the API's `services/forewordPageImage.js`. Keep the two in step:
 * printableText.test.js pins the same cases as the API's test, and printGlyphs.json comes from the
 * print fonts (API `scripts/buildPrintGlyphs.py`, which the API's renderer reads too).
 */

/** The font every emoji prints in. Goes right after the page's own font in a font-family. */
export const PRINT_EMOJI_FONT = "'Print Emoji'";

const SOLID_HEART = "\u2665";
const SINGLE_HEARTS = new Set([..."\u2665\u2764\u{1F499}\u{1F49A}\u{1F49B}\u{1F49C}\u{1F5A4}\u{1F90D}\u{1F90E}\u{1F9E1}\u{1FA75}\u{1FA76}\u{1FA77}"]);
const JOINED = new Set(printGlyphs.joined);
const TONE_BASES = new Set(printGlyphs.toneBases);

const VARIATION = /[\uFE0E\uFE0F]/gu;
const INVISIBLE_IN_EMOJI = /[\u200D\uFE0E\uFE0F]/gu;
const SKIN_TONE = /[\u{1F3FB}-\u{1F3FF}]/gu;

const graphemes = typeof Intl !== "undefined" && Intl.Segmenter ? new Intl.Segmenter("en", { granularity: "grapheme" }) : null;
const clustersOf = (text) => (graphemes ? Array.from(graphemes.segment(text), (s) => s.segment) : Array.from(text));

const IS_EMOJI = /^\p{Extended_Pictographic}$/u;

/**
 * A joiner only means something between two emoji (a family, a flag). Stepped through by hand: a
 * lookbehind breaks the whole app on older Safari, and a regular expression for this answers
 * differently across JavaScript engines (a toned emoji fails on Node 26, passes on 22).
 */
export const withoutStrayJoiners = (text) =>
  text.replace(/\u200D/g, (joiner, at, whole) => {
    const before = Array.from(whole.slice(0, at));
    let previous = before.pop();
    if (previous === "\uFE0F" || (previous >= "\u{1F3FB}" && previous <= "\u{1F3FF}")) previous = before.pop();
    const next = Array.from(whole.slice(at + 1, at + 3))[0];
    return previous && next && IS_EMOJI.test(previous) && IS_EMOJI.test(next) ? joiner : "";
  });

function printFontsHave(codePoint) {
  const { ranges } = printGlyphs;
  let low = 0;
  let high = ranges.length - 1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    if (codePoint < ranges[mid][0]) high = mid - 1;
    else if (codePoint > ranges[mid][1]) low = mid + 1;
    else return true;
  }
  return false;
}

const fontsCanDraw = (cluster) => Array.from(cluster.replace(INVISIBLE_IN_EMOJI, "")).every((char) => printFontsHave(char.codePointAt(0)));

/** Whether the emoji font draws these joined code points as one picture rather than separate pieces. */
function drawsAsOnePicture(cluster) {
  const bare = cluster.replace(VARIATION, "");
  const withoutTones = bare.replace(SKIN_TONE, "");
  if (withoutTones === bare) return Array.from(bare).length === 1 || JOINED.has(bare);
  const parts = Array.from(withoutTones);
  if (parts.length === 1) return TONE_BASES.has(parts[0].codePointAt(0));
  return JOINED.has(withoutTones);
}

function printableCluster(cluster) {
  if (SINGLE_HEARTS.has(cluster.replace(VARIATION, ""))) return SOLID_HEART;
  // A letter with a mark no font has keeps the letter: only emoji are all or nothing.
  if (!/\p{Extended_Pictographic}|\p{Regional_Indicator}|\u20E3/u.test(cluster)) {
    return Array.from(cluster.replace(INVISIBLE_IN_EMOJI, "")).filter((char) => printFontsHave(char.codePointAt(0))).join("");
  }
  if (!fontsCanDraw(cluster)) return "";
  const isSequence = cluster.includes("\u200D") || cluster.includes("\u20E3") || /\p{Regional_Indicator}/u.test(cluster);
  const hasSkinTone = cluster.search(SKIN_TONE) > 0;
  if (!isSequence && !hasSkinTone) return cluster;
  if (drawsAsOnePicture(cluster)) return cluster;
  const withoutTone = cluster.replace(SKIN_TONE, "");
  return hasSkinTone && (!withoutTone.includes("\u200D") || drawsAsOnePicture(withoutTone)) ? withoutTone : "";
}

const withoutInvisible = (text) => String(text || "").replace(/[\s\u200B-\u200D\u2060\uFEFF\uFE0E\uFE0F]/g, "");

/** Whether a title would put anything on the cover. Nobody moves on from a title step without one. */
export const titleCanPrint = (title) => withoutInvisible(printableText(title)) !== "";

/** What to tell someone whose title has nothing we can print, or null when it's fine. Plain words: many customers are older. */
export const titleProblem = (title) => {
  if (titleCanPrint(title)) return null;
  // Only invisible characters looks like an empty box, so it gets the same message as one.
  return withoutInvisible(title) ? "We can't print the emoji or characters in this title. Please add some letters or numbers." : "Please give your book a title.";
};

/**
 * Text ready to draw in a preview. Chrome reads the invisible "show in colour" marker some emoji carry
 * as a reason to skip the print font. Only for drawing: line breaks count it, as print does.
 */
export const forPreview = (text) => text.replace(VARIATION, "");

/** Leaves out what the print fonts can't draw. A line left empty goes too. */
export function printableText(raw) {
  const given = String(raw || "");
  if (/^[\x00-\x7F]*$/.test(given)) return given;
  // Titles and headings reach here untidied. Glued to a letter, a stray joiner would look like a broken emoji.
  const text = withoutStrayJoiners(given);

  const lines = [];
  for (const line of text.split("\n")) {
    let kept = "";
    for (const cluster of clustersOf(line)) {
      kept += /^\s+$/.test(cluster) ? cluster : printableCluster(cluster);
    }
    if (line.trim() && !kept.trim()) continue;
    lines.push(kept === line ? line : kept.replace(/ {2,}/g, " ").trim());
  }
  return lines.join("\n");
}
